import React, { useState, useEffect } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Edit3, Check, Wand2 } from 'lucide-react';

interface EditablePromptInlineProps {
  initialPrompt: string;
  recipeId: string;
  onGenerate: (prompt: string) => void;
  isGenerating: boolean;
  enhancedText?: string; // New prop
}

export const EditablePromptInline: React.FC<EditablePromptInlineProps> = ({
  initialPrompt,
  onGenerate,
  isGenerating,
  enhancedText
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState(initialPrompt);
  const hasChanges = editedPrompt !== initialPrompt;

  const MAX_PROMPT_LENGTH = 1000;
  const characterCount = editedPrompt.length;
  const isOverLimit = characterCount > MAX_PROMPT_LENGTH;

  useEffect(() => {
    if (isEditing) {
      // Auto-focus and select text can be added here if desired
    }
  }, [isEditing]);

  const handleEditClick = () => setIsEditing(true);
  const handleCancelEdit = () => {
    setEditedPrompt(initialPrompt);
    setIsEditing(false);
  };

  const handleGenerateWithChanges = () => {
    if (!isOverLimit) {
      onGenerate(editedPrompt);
      setIsEditing(false);
    }
  };

  const handleGenerateOriginal = () => onGenerate(initialPrompt);
  const handleResetToOriginal = () => setEditedPrompt(initialPrompt);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancelEdit();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleGenerateWithChanges();
    }
  };

  const Spinner = () => (
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
  );

  if (isEditing) {
    return (
      <div className="my-2 space-y-3 w-full">
        <TextareaAutosize
          value={editedPrompt}
          onChange={(e) => setEditedPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          className={`w-full p-2 border rounded-md resize-none font-mono text-sm
            ${isOverLimit ? 'border-red-400 bg-red-50' : 'border-gray-300 dark:border-gray-600'}
            focus:ring-2 focus:ring-blue-500 outline-none`}
          disabled={isGenerating}
          minRows={6}
          maxRows={15}
        />
        
        <div className="flex justify-between items-center text-xs text-gray-500">
          <span className={isOverLimit ? 'text-red-500 font-medium' : ''}>
            {characterCount}/{MAX_PROMPT_LENGTH}
          </span>
          {hasChanges && <span className="text-amber-500 font-medium">Modified</span>}
        </div>

        <div className="flex gap-2 items-center">
          <Button
            size="sm"
            onClick={handleGenerateWithChanges}
            disabled={isGenerating || isOverLimit}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isGenerating ? <Spinner /> : <Check className="h-4 w-4" />}
            <span className="ml-2">Generate</span>
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancelEdit} disabled={isGenerating}>
            Cancel
          </Button>
          {hasChanges && (
            <Button size="sm" variant="ghost" onClick={handleResetToOriginal} disabled={isGenerating}>
              Reset
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="my-2 space-y-3">
      <div className="bg-gray-100 dark:bg-gray-800 rounded-md p-3 text-sm font-mono text-gray-800 dark:text-gray-200">
        <ReactMarkdown
          components={{
            p: ({node: _node, ...props}) => <p className="my-0" {...props} />,
            // Updated strong styling to be bold and blue
            strong: ({node: _node, ...props}) => <strong className="font-bold text-blue-600 dark:text-blue-400" {...props} />,
          }}
        >
          {(() => {
            let displayPromptForMarkdown = initialPrompt;
            const knownPrefix = "Professional food photography, overhead shot of ";
            if (enhancedText && initialPrompt.startsWith(knownPrefix + enhancedText)) {
              displayPromptForMarkdown = knownPrefix + `**${enhancedText}**` + initialPrompt.substring(knownPrefix.length + enhancedText.length);
            }
            return displayPromptForMarkdown;
          })()}
        </ReactMarkdown>
      </div>

      <div className="flex gap-2 items-center">
        <Button
          size="sm"
          onClick={handleGenerateOriginal}
          disabled={isGenerating}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isGenerating ? <Spinner /> : <Wand2 className="h-4 w-4" />}
          <span className="ml-2">Generate Image</span>
        </Button>
        <Button size="sm" variant="outline" onClick={handleEditClick} disabled={isGenerating}>
          <Edit3 className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>
    </div>
  );
};

export default EditablePromptInline;